// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { createReference, type MedplumClient } from '@medplum/core';
import type {
  Coverage,
  DocumentReference,
  Encounter,
  GuidanceResponse,
  MedicationRequest,
  Patient,
  Practitioner,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import polarisLogo from '../assets/polaris-logo.svg?raw';
import { fetchPrimaryCoverage, resolvePayorOrganization } from './patient-coverage';
import { withTenantScope } from './tenant';

export const REFERRAL_DOCUMENT_TYPE_SYSTEM = 'https://polarissleep.com/fhir/document-type';
export const REFERRAL_DOCUMENT_TYPE_CODE = 'referral-form';
export const REFERRAL_DOCUMENT_TITLE = 'Referral Form';
export const REFERRAL_ORDER_SYSTEM = 'https://polarissleep.com/fhir/order-type';

function name(resource: Patient | Practitioner): string {
  const entry = resource.name?.[0];
  if (entry?.text) {
    return entry.text;
  }
  const base = [...(entry?.given ?? []), entry?.family].filter(Boolean).join(' ');
  const suffix = resource.resourceType === 'Practitioner' ? entry?.suffix?.filter(Boolean).join(', ') : undefined;
  return [base, suffix ? `, ${suffix}` : undefined].filter(Boolean).join(' ') || '—';
}

/** Labels the referral note is written with; a value runs until the next labelled line, so multi-line text survives. */
const NOTE_LABELS = ['Reason', 'Referral target', 'Special instructions', 'Equipment instructions', 'Task notes'];

function noteValue(order: MedicationRequest, label: string): string | undefined {
  const prefix = `${label}:`;
  for (const note of order.note ?? []) {
    const lines = note.text?.split('\n') ?? [];
    const start = lines.findIndex((text) => text.startsWith(prefix));
    if (start === -1) {
      continue;
    }
    const value = [lines[start].slice(prefix.length)];
    for (const line of lines.slice(start + 1)) {
      if (NOTE_LABELS.some((other) => line.startsWith(`${other}:`))) {
        break;
      }
      value.push(line);
    }
    return value.join('\n').trim();
  }
  return undefined;
}

function referralType(order: MedicationRequest): string {
  return order.medicationCodeableConcept?.text?.split(' · ')[0] ?? 'Referral';
}

function diagnosis(order: MedicationRequest): string {
  const reason = order.reasonCode?.[0];
  const coding = reason?.coding?.[0];
  return coding
    ? `${coding.display ?? coding.code ?? '—'}${coding.code ? ` (${coding.code})` : ''}`
    : (reason?.text ?? '—');
}

function formatDate(value: string | undefined): string {
  const parsed = value ? dayjs(value) : undefined;
  return parsed?.isValid() ? parsed.format('MMMM D, YYYY') : '—';
}

function formatDateTime(value: string): string {
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return value;
  }
  const zone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(parsed.toDate())
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${parsed.format('MMMM D, YYYY [at] h:mm A')}${zone ? ` ${zone}` : ''}`;
}

function formatEncounter(encounter: Encounter | undefined): string {
  if (!encounter) {
    return '—';
  }
  const type = encounter.type?.[0]?.text ?? encounter.type?.[0]?.coding?.[0]?.display ?? 'Encounter';
  return `${type} · ${formatDate(encounter.period?.start)}`;
}

function pdfField(label: string, value: string | undefined, wide = false): Record<string, unknown> {
  return {
    ...(wide ? { colSpan: 2 } : {}),
    stack: [
      { text: label.toUpperCase(), style: 'fieldLabel' },
      { text: value?.trim() || '—', style: 'fieldValue' },
    ],
    margin: [0, 0, 0, 8],
  };
}

function fieldGrid(fields: Record<string, unknown>[]): Record<string, unknown> {
  const body: Record<string, unknown>[][] = [];
  for (let index = 0; index < fields.length; ) {
    const field = fields[index];
    if (field?.colSpan === 2) {
      body.push([field, {}]);
      index += 1;
      continue;
    }
    const next = fields[index + 1];
    body.push([field, next ?? {}]);
    index += 2;
  }
  return {
    table: {
      widths: ['*', '*'],
      body,
    },
    layout: 'noBorders',
  };
}

function createPdfBlob(definition: Record<string, unknown>): Promise<Blob> {
  return Promise.all([import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')]).then(
    ([makeModule, fontsModule]) => {
      const pdfMake = makeModule.default;
      const fonts = fontsModule.default;
      pdfMake.vfs = fonts.pdfMake?.vfs ?? fonts.vfs ?? (fonts as unknown as Record<string, string>);
      return new Promise<Blob>((resolve, reject) => {
        try {
          pdfMake.createPdf(definition).getBlob(resolve);
        } catch (error) {
          reject(error);
        }
      });
    }
  );
}

function svgToPngDataUri(svg: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 294;
      canvas.height = 56;
      const context = canvas.getContext('2d');
      if (!context) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to prepare Polaris logo for PDF'));
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to load Polaris logo for PDF'));
    };
    image.src = objectUrl;
  });
}

function isReferralOrder(order: MedicationRequest): boolean {
  const isOralAppliance =
    order.medicationCodeableConcept?.coding?.some(
      (coding) => coding.system === 'http://polarissleep.com/therapies' && coding.code === 'oral-appliance-therapy'
    ) ?? false;
  const orderText = order.medicationCodeableConcept?.text?.toLowerCase() ?? '';
  return isOralAppliance || orderText.includes('referral') || Boolean(noteValue(order, 'Referral target'));
}

export function isReferralMedicationRequest(order: MedicationRequest): boolean {
  return isReferralOrder(order);
}

export function isReferralServiceRequest(order: ServiceRequest): boolean {
  return (
    order.code?.coding?.some((coding) => coding.system === REFERRAL_ORDER_SYSTEM && coding.code === 'referral') ?? false
  );
}

function serviceRequestReferralOrder(order: ServiceRequest): MedicationRequest {
  return {
    resourceType: 'MedicationRequest',
    status: 'active',
    intent: 'order',
    priority: order.priority ?? 'routine',
    subject: order.subject as MedicationRequest['subject'],
    encounter: order.encounter,
    requester: order.requester,
    authoredOn: order.authoredOn,
    medicationCodeableConcept: { text: order.code?.text ?? 'Referral' },
    reasonCode: order.reasonCode,
    note: [
      ...(order.note ?? []).map((note) =>
        note.text?.startsWith('Clinical notes:')
          ? { text: `Special instructions:${note.text.slice('Clinical notes:'.length)}` }
          : note
      ),
      order.performer?.[0]?.display ? { text: `Referral target: ${order.performer[0].display}` } : undefined,
    ].filter((note): note is { text: string } => Boolean(note?.text)),
  };
}

export function buildReferralPdfDefinition(
  patient: Patient,
  order: MedicationRequest,
  practitioner: Practitioner,
  coverage: Coverage | undefined,
  payorName: string | undefined,
  encounter: Encounter | undefined,
  signedAt: string,
  logoDataUri?: string
): Record<string, unknown> {
  const insurance = [payorName, coverage?.subscriberId ? `Member ${coverage.subscriberId}` : undefined]
    .filter(Boolean)
    .join(' · ');
  const clinicalNotes = [noteValue(order, 'Equipment instructions'), noteValue(order, 'Special instructions')]
    .filter(Boolean)
    .join('\n\n');
  const referralTarget = noteValue(order, 'Referral target');
  const npi = practitioner.identifier?.find((item) => item.system?.toLowerCase().includes('npi'))?.value;

  return {
    pageSize: 'LETTER',
    pageMargins: [61, 61, 61, 61],
    content: [
      {
        columns: [
          {
            stack: [
              ...(logoDataUri
                ? [{ image: 'polarisLogo', width: 147, height: 28 }]
                : [{ text: 'POLARIS', style: 'brand' }]),
              { text: 'SLEEP MEDICINE', style: 'brandSub' },
            ],
          },
          {
            text: '1032 15th St NW #418\nWashington, DC 20005\n(301) 246-2152 · fax (260) 668-2001\nEIN 41-4815927',
            alignment: 'right',
            style: 'organization',
          },
        ],
        margin: [0, 0, 0, 16],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 480, y2: 0, lineWidth: 1.5, lineColor: '#230415' }] },
      { text: 'Referral Form', style: 'title' },
      { text: formatDate(order.authoredOn ?? signedAt), style: 'subtitle' },
      { text: 'PATIENT', style: 'section' },
      fieldGrid([
        pdfField('Name', name(patient)),
        pdfField('Date of birth', formatDate(patient.birthDate)),
        pdfField('Phone', patient.telecom?.find((item) => item.system === 'phone')?.value),
        pdfField('Insurance', insurance),
      ]),
      { text: 'REFERRAL', style: 'section' },
      fieldGrid([
        pdfField('Referral type', referralType(order)),
        pdfField(
          'Priority',
          order.priority ? order.priority.charAt(0).toUpperCase() + order.priority.slice(1) : 'Routine'
        ),
        pdfField('Refer to', referralTarget, true),
        pdfField('Diagnosis', diagnosis(order)),
        pdfField('Reason', noteValue(order, 'Reason')),
      ]),
      { text: 'CLINICAL NOTES', style: 'section' },
      { text: clinicalNotes || '—', style: 'clinicalNote' },
      { text: 'REFERRAL FROM', style: 'section' },
      fieldGrid([
        // NPs and care coordinators can sign referrals too, so the label is not physician-specific.
        pdfField('Referring provider', name(practitioner)),
        pdfField('NPI', npi),
        pdfField('Practice', 'Polaris Sleep'),
        pdfField('Encounter', formatEncounter(encounter)),
      ]),
      {
        stack: [
          { text: name(practitioner), style: 'signatureName' },
          { text: `Electronically signed ${formatDateTime(signedAt)}`, style: 'signatureMeta' },
          ...(npi ? [{ text: `NPI ${npi}`, style: 'signatureMeta' }] : []),
        ],
        margin: [0, 28, 0, 0],
      },
    ],
    styles: {
      brand: { fontSize: 20, bold: true, color: '#230415' },
      brandSub: { fontSize: 8, color: '#6b6169', characterSpacing: 1.1, margin: [0, 5, 0, 0] },
      organization: { fontSize: 8, color: '#6b6169', lineHeight: 1.35 },
      title: { fontSize: 15, bold: true, color: '#241d22', margin: [0, 15, 0, 3] },
      subtitle: { fontSize: 9, color: '#6b6169', margin: [0, 0, 0, 22] },
      section: { fontSize: 8.5, bold: true, color: '#230415', characterSpacing: 1, margin: [0, 0, 0, 8] },
      fieldLabel: { fontSize: 7.5, color: '#6b6169', characterSpacing: 0.5, margin: [0, 0, 0, 2] },
      fieldValue: { fontSize: 10, color: '#241d22' },
      clinicalNote: { fontSize: 9.5, color: '#241d22', fillColor: '#f7f2f5', margin: [0, 0, 0, 20], padding: 10 },
      signatureName: { fontSize: 17, italics: true, margin: [0, 7, 0, 5] },
      signatureMeta: { fontSize: 8, color: '#6b6169', margin: [0, 0, 0, 2] },
    },
    images: logoDataUri ? { polarisLogo: logoDataUri } : {},
    defaultStyle: { fontSize: 9.5 },
  };
}

export async function createReferralDocument(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  order: MedicationRequest,
  signedAt = new Date().toISOString()
): Promise<DocumentReference> {
  if (!order.id) {
    throw new Error('MedicationRequest must have an id before creating its referral document');
  }
  const [coverage, encounter] = await Promise.all([
    patient.id ? fetchPrimaryCoverage(medplum, patient.id) : Promise.resolve(undefined),
    order.encounter?.reference
      ? medplum.readReference<Encounter>(order.encounter).catch(() => undefined)
      : Promise.resolve(undefined),
  ]);
  const payor = coverage ? await resolvePayorOrganization(medplum, coverage) : undefined;
  const logoDataUri = await svgToPngDataUri(polarisLogo);
  const blob = await createPdfBlob(
    buildReferralPdfDefinition(patient, order, practitioner, coverage, payor?.name, encounter, signedAt, logoDataUri)
  );
  const safeName =
    name(patient)
      .replaceAll(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'patient';
  const binary = await medplum.createBinary(blob, `${safeName}-referral-form.pdf`, 'application/pdf');
  return medplum.createResource(
    withTenantScope<DocumentReference>({
      resourceType: 'DocumentReference',
      status: 'current',
      subject: createReference(patient),
      date: signedAt,
      author: [createReference(practitioner)],
      type: { coding: [{ system: REFERRAL_DOCUMENT_TYPE_SYSTEM, code: REFERRAL_DOCUMENT_TYPE_CODE }] },
      description: `${REFERRAL_DOCUMENT_TITLE} linked to MedicationRequest/${order.id}.`,
      context: { related: [{ reference: `MedicationRequest/${order.id}` }] },
      content: [
        { attachment: { title: REFERRAL_DOCUMENT_TITLE, url: `Binary/${binary.id}`, contentType: 'application/pdf' } },
      ],
    })
  );
}

export async function createServiceRequestReferralDocument(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  order: ServiceRequest,
  signedAt = new Date().toISOString()
): Promise<DocumentReference> {
  if (!order.id) {
    throw new Error('ServiceRequest must have an id before creating its referral document');
  }
  const referralOrder = serviceRequestReferralOrder(order);
  const [coverage, encounter] = await Promise.all([
    patient.id ? fetchPrimaryCoverage(medplum, patient.id) : Promise.resolve(undefined),
    order.encounter?.reference
      ? medplum.readReference<Encounter>(order.encounter).catch(() => undefined)
      : Promise.resolve(undefined),
  ]);
  const payor = coverage ? await resolvePayorOrganization(medplum, coverage) : undefined;
  const logoDataUri = await svgToPngDataUri(polarisLogo);
  const blob = await createPdfBlob(
    buildReferralPdfDefinition(
      patient,
      referralOrder,
      practitioner,
      coverage,
      payor?.name,
      encounter,
      signedAt,
      logoDataUri
    )
  );
  const safeName =
    name(patient)
      .replaceAll(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'patient';
  const binary = await medplum.createBinary(blob, `${safeName}-referral-form.pdf`, 'application/pdf');
  return medplum.createResource(
    withTenantScope<DocumentReference>({
      resourceType: 'DocumentReference',
      status: 'current',
      subject: createReference(patient),
      date: signedAt,
      author: [createReference(practitioner)],
      type: { coding: [{ system: REFERRAL_DOCUMENT_TYPE_SYSTEM, code: REFERRAL_DOCUMENT_TYPE_CODE }] },
      description: `${REFERRAL_DOCUMENT_TITLE} linked to ServiceRequest/${order.id}.`,
      context: { related: [{ reference: `ServiceRequest/${order.id}` }] },
      content: [
        { attachment: { title: REFERRAL_DOCUMENT_TITLE, url: `Binary/${binary.id}`, contentType: 'application/pdf' } },
      ],
    })
  );
}

export function attachReferralDocumentToTask(task: Task, document: DocumentReference): Task {
  return {
    ...task,
    input: [
      ...(task.input ?? []).filter((input) => input.type?.coding?.[0]?.code !== REFERRAL_DOCUMENT_TYPE_CODE),
      {
        type: { coding: [{ system: REFERRAL_DOCUMENT_TYPE_SYSTEM, code: REFERRAL_DOCUMENT_TYPE_CODE }] },
        valueReference: createReference(document),
      },
    ],
  };
}

function guidanceResponseReferralOrder(response: GuidanceResponse): MedicationRequest {
  const recipient = response.extension?.find((extension) => extension.url?.includes('performer'))?.valueReference;
  const reason = response.note
    ?.map((note) => note.text)
    .filter(Boolean)
    .join('\n');
  const referralType = response.moduleUri?.split('/').at(-1)?.replaceAll('-', ' ') ?? 'Referral';
  const note = [
    reason ? `Reason: ${reason}` : undefined,
    recipient?.display ? `Referral target: ${recipient.display}` : undefined,
  ]
    .filter(Boolean)
    .join('\n');
  return {
    resourceType: 'MedicationRequest',
    status: 'active',
    intent: 'order',
    priority: 'routine',
    subject: response.subject ?? { reference: 'Patient/unknown' },
    authoredOn: response.occurrenceDateTime,
    medicationCodeableConcept: { text: referralType },
    note: note ? [{ text: note }] : undefined,
  };
}

export async function downloadGuidanceResponseReferralPdf(
  medplum: MedplumClient,
  patient: Patient,
  response: GuidanceResponse,
  practitioner: Practitioner
): Promise<void> {
  const coverage = patient.id ? await fetchPrimaryCoverage(medplum, patient.id) : undefined;
  const payor = coverage ? await resolvePayorOrganization(medplum, coverage) : undefined;
  const logoDataUri = await svgToPngDataUri(polarisLogo);
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = pdfMakeModule.default;
  const pdfFonts = pdfFontsModule.default;
  pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? (pdfFonts as unknown as Record<string, string>);
  const definition = buildReferralPdfDefinition(
    patient,
    guidanceResponseReferralOrder(response),
    practitioner,
    coverage,
    payor?.name,
    undefined,
    response.occurrenceDateTime ?? new Date().toISOString(),
    logoDataUri
  );
  const safeName =
    name(patient)
      .replaceAll(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'patient';
  pdfMake.createPdf(definition).download(`${safeName}-referral-form.pdf`);
}

export async function viewServiceRequestReferralPdf(
  medplum: MedplumClient,
  patient: Patient,
  order: ServiceRequest,
  practitioner: Practitioner
): Promise<void> {
  const [coverage, encounter] = await Promise.all([
    patient.id ? fetchPrimaryCoverage(medplum, patient.id) : Promise.resolve(undefined),
    order.encounter?.reference
      ? medplum.readReference<Encounter>(order.encounter).catch(() => undefined)
      : Promise.resolve(undefined),
  ]);
  const payor = coverage ? await resolvePayorOrganization(medplum, coverage) : undefined;
  const logoDataUri = await svgToPngDataUri(polarisLogo);
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = pdfMakeModule.default;
  const pdfFonts = pdfFontsModule.default;
  pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? (pdfFonts as unknown as Record<string, string>);
  const definition = buildReferralPdfDefinition(
    patient,
    serviceRequestReferralOrder(order),
    practitioner,
    coverage,
    payor?.name,
    encounter,
    new Date().toISOString(),
    logoDataUri
  );
  const blob = await new Promise<Blob>((resolve, reject) => {
    try {
      pdfMake.createPdf(definition).getBlob(resolve);
    } catch (error) {
      reject(error);
    }
  });
  window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
}
